import type { HttpRequest, HttpResponse, Interceptor, InterceptorContext, TransportAdapter } from '../types'

const DEFAULT_ORDER = 100

function getEnvironment(): 'server' | 'client' {
  if (typeof window !== 'undefined') return 'client'
  return 'server'
}

export class InterceptorPipeline {
  private interceptors: Interceptor[] = []

  use(interceptor: Interceptor): this {
    this.interceptors.push(interceptor)
    return this
  }

  remove(name: string): this {
    this.interceptors = this.interceptors.filter(i => i.name !== name)
    return this
  }

  async execute<T>(request: HttpRequest, transport: TransportAdapter): Promise<HttpResponse<T>> {
    const environment = getEnvironment()
    const sorted = [...this.interceptors]
      .filter(i => !i.environments || i.environments.length === 0 || i.environments.includes(environment))
      .sort((a, b) => (a.order ?? DEFAULT_ORDER) - (b.order ?? DEFAULT_ORDER))

    const ctx: InterceptorContext = {
      request: { ...request, headers: { ...request.headers } },
      metadata: new Map(),
      environment,
      startTime: Date.now(),
    }

    let index = 0
    const next = async (): Promise<void> => {
      if (ctx.error) return
      if (index >= sorted.length) {
        const response = await transport.execute<T>(ctx.request)
        ctx.response = response
        return
      }
      const interceptor = sorted[index++]
      if (interceptor == null) throw new Error('Interceptor pipeline invariant')
      await interceptor.handler(ctx, next)
    }

    await next()

    if (ctx.error) throw ctx.error
    if (ctx.response == null) {
      throw new Error('Interceptor pipeline did not produce a response')
    }
    return ctx.response as HttpResponse<T>
  }
}
